import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';

// Google's brand marks, kept in one place so the sign-in button and the
// Places attribution can't drift apart — and so there is a single file to
// point a compliance reviewer at.
//
// The "G" below is Google's official four-colour mark with their exact brand
// hex values, reproduced unaltered. Google's guidelines forbid recolouring,
// rotating, adding effects to, or changing the proportions of the mark.

export function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  );
}

/**
 * Required attribution wherever Google Places data (venue name, address,
 * rating, opening hours…) is shown OUTSIDE a Google Map. The Places API
 * policies require a visible "Powered by Google" credit in that case — this
 * app shows venue details with no map, so it applies to every surface that
 * renders a Places-sourced venue.
 *
 * Render it only for venues whose `sourceType` is 'GOOGLE_PLACES'; venues
 * entered manually into our own DB carry no such obligation.
 *
 * NOTE FOR REVIEW: Google also publishes fixed "Powered by Google" image
 * assets (on-white and on-non-white variants). If legal wants byte-exact
 * conformance rather than a faithful reproduction, drop those files into
 * public/ and swap the markup here — this component is the only place that
 * needs to change.
 */
export function PoweredByGoogle({ t, style }: { t: Theme; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontFamily: FONTS.sans, fontSize: 10.5, letterSpacing: 0.2,
        color: t.inkMute, whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span>Powered by</span>
      <GoogleG size={12} />
      <span style={{ fontWeight: 500 }}>Google</span>
    </div>
  );
}
